import { useState, useMemo } from "react";
import { Clock, CheckCircle2, ShieldAlert, Inbox } from "lucide-react";
import { PageHeader, Panel } from "@/components/dashboard/primitives";
import { RiskBadge, fmtUSD } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Loading from "@/components/dashboard/Loading";
import { useDashboard, useDashboardActions } from "@/hooks/useDashboard";
import { toast } from "sonner";

const filters = ["All", "High Priority", "New", "Expiring"];

export default function Approvals() {
  const { data, isLoading } = useDashboard();
  const { decideApproval } = useDashboardActions();
  const [filter, setFilter] = useState("All");
  const [detail, setDetail] = useState(null);

  const items = data?.approvals || [];

  const visible = useMemo(() => {
    return items.filter((a) => {
      if (filter === "All") return true;
      if (filter === "High Priority") return a.priority === "High";
      if (filter === "New") return a.tag === "New";
      if (filter === "Expiring") return a.tag === "Expiring";
      return true;
    });
  }, [items, filter]);

  if (isLoading || !data) {
    return (<div><PageHeader title="Approvals" subtitle="Review transactions that require your attention." /><Loading /></div>);
  }

  const decide = async (id, approve) => {
    await decideApproval(id, approve ? "approve" : "reject");
    setDetail(null);
    toast.success(`Approval ${approve ? "granted" : "denied"}`);
  };

  return (
    <div>
      <PageHeader title="Approvals" subtitle="Review transactions that require your attention." />

      <div className="flex flex-wrap gap-2 mb-5">
        {filters.map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`rounded-full px-4 py-1.5 text-sm transition-colors ${filter === f ? "bg-[#4F8CFF] text-white" : "border border-white/12 text-slate-300 hover:bg-white/5"}`}
            data-testid={`approval-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}>
            {f}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <Panel className="p-12 text-center" data-testid="approvals-empty">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-500/12 text-emerald-400"><Inbox className="h-7 w-7" /></div>
          <p className="mt-4 text-white font-medium">All caught up</p>
          <p className="mt-1 text-sm text-slate-400">No approvals match this filter right now.</p>
        </Panel>
      ) : (
        <div className="space-y-4">
          {visible.map((a) => (
            <Panel key={a.id} className="p-5 vora-card-hover" data-testid={`approval-card-${a.id}`}>
              <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
                <div className="flex items-start gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500/12 text-amber-400 shrink-0"><Clock className="h-5 w-5" /></span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-white">{a.agent} → {a.merchant}</p>
                      <RiskBadge risk={a.risk} />
                      {a.tag && <span className="text-[10px] font-mono uppercase tracking-wider rounded-full border border-white/12 px-2 py-0.5 text-slate-400">{a.tag}</span>}
                    </div>
                    <p className="text-lg font-semibold text-white mt-1">{fmtUSD(a.amount)}</p>
                    <p className="text-sm text-slate-400 mt-1">{a.reason}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1"><ShieldAlert className="h-3.5 w-3.5" /> {a.policy}</span>
                      <span>{a.time}</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button onClick={() => decide(a.id, true)} className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-5" data-testid={`approve-transaction-btn-${a.id}`}>Approve</Button>
                  <Button onClick={() => decide(a.id, false)} variant="outline" className="bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-full px-5" data-testid={`reject-transaction-btn-${a.id}`}>Reject</Button>
                  <Button onClick={() => setDetail(a)} variant="ghost" className="text-slate-300 hover:text-white hover:bg-white/5 rounded-full" data-testid={`view-details-btn-${a.id}`}>View Details</Button>
                </div>
              </div>
            </Panel>
          ))}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[#0B1526] border-white/10 text-white max-w-lg">
          {detail && (
            <>
              <DialogHeader><DialogTitle>Approval request</DialogTitle></DialogHeader>
              <div className="mt-2">
                <p className="text-3xl font-semibold">{fmtUSD(detail.amount)}</p>
                <div className="mt-2"><RiskBadge risk={detail.risk} /></div>
                <div className="mt-5 grid grid-cols-2 gap-y-3 text-sm">
                  <span className="text-slate-400">Agent</span><span className="text-white text-right">{detail.agent}</span>
                  <span className="text-slate-400">Merchant</span><span className="text-white text-right">{detail.merchant}</span>
                  <span className="text-slate-400">Policy triggered</span><span className="text-white text-right">{detail.policy}</span>
                  <span className="text-slate-400">Requested</span><span className="text-white text-right">{detail.time}</span>
                </div>
                <div className="mt-5 rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
                  <p className="text-xs font-mono uppercase tracking-wider text-amber-400">Reason</p>
                  <p className="mt-1 text-sm text-slate-200">{detail.reason}</p>
                </div>
                <div className="mt-6 flex gap-3">
                  <Button onClick={() => decide(detail.id, true)} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full gap-1.5" data-testid="detail-approve-btn"><CheckCircle2 className="h-4 w-4" /> Approve</Button>
                  <Button onClick={() => decide(detail.id, false)} variant="outline" className="flex-1 bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-full" data-testid="detail-reject-btn">Reject</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
